import { useEffect, useRef, useState } from 'react';
import { FiHelpCircle } from 'react-icons/fi';
import { askDoubt } from '@/api/support.api';
import { Modal } from '@/components/common/Modal';
import { useAuth } from '@/features/auth/AuthContext';

/**
 * AskMAK 24/7 doubt-solve chat — doubtModal + openDoubt()/askDoubt()
 * (index.html:4300). A chat box that posts each question to
 * POST /courses/:slug/doubt, which returns an instant answer matched from the
 * course question bank or a "logged for a mentor" reply (see community.askDoubt).
 *
 * Opened from the home "AskMAK 24/7" pillar and the player tools row. Login is
 * required (the endpoint is authed) — the UI action gates that before opening.
 */
interface Msg {
  who: 'bot' | 'you';
  text: string;
}

export function DoubtModal({ courseSlug, onClose }: { courseSlug: string; onClose: () => void }) {
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] ?? 'there';
  const [log, setLog] = useState<Msg[]>([
    { who: 'bot', text: `Hi ${firstName}! Ask me anything about your course. If I can't help, I'll pass it to a mentor.` },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log]);

  async function ask(): Promise<void> {
    const q = input.trim();
    if (!q || busy) return;
    setInput('');
    setLog((l) => [...l, { who: 'you', text: q }]);
    setBusy(true);
    try {
      const data = await askDoubt(courseSlug, q);
      setLog((l) => [...l, { who: 'bot', text: data.reply }]);
    } catch {
      setLog((l) => [...l, { who: 'bot', text: 'Sorry — something went wrong sending that. Please try again.' }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} label="Doubt solve">
      <h2 className="inline-flex items-center gap-2 text-lg">
        <FiHelpCircle className="size-5 shrink-0 text-[var(--cobalt)]" aria-hidden />
        Doubt solve — 24/7
      </h2>
      <p className="mb-5 text-sm text-[var(--ink-soft)]">Instant answers from the assistant. Anything it can&apos;t solve goes to a mentor.</p>
      <div className="mb-2.5 grid max-h-[280px] gap-2.5 overflow-y-auto rounded-xl border border-[var(--line)] bg-[var(--paper)] p-3" data-testid="doubt-log" ref={logRef}>
        {log.map((m, i) => (
          <div className={`max-w-[88%] rounded-xl px-3 py-[9px] text-[13.5px] ${m.who === 'bot' ? 'justify-self-start border border-[var(--line)] bg-[var(--card)]' : 'justify-self-end bg-[var(--ink)] text-white'}`} key={i}>
            <b className="mb-0.5 block text-[11.5px] opacity-70">{m.who === 'bot' ? 'MAKTech Assistant' : 'You'}</b>
            <p>{m.text}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        <input
          data-testid="doubt-input"
          placeholder="Type your question…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void ask();
          }}
          className="min-w-[140px] flex-1 rounded-[10px] border-[1.5px] border-[var(--line)] px-3 py-2.5 text-[13.5px] outline-none focus:border-[var(--cobalt)]"
        />
        <button className="inline-flex items-center justify-center rounded-full bg-linear-to-br from-[var(--ember)] to-[var(--cobalt)] px-4 py-[9px] text-[13.5px] font-semibold text-white shadow-[var(--shadow-orange)] disabled:opacity-60" onClick={() => void ask()} disabled={busy} data-testid="doubt-ask">
          {busy ? '…' : 'Ask'}
        </button>
      </div>
      <p className="mt-2.5 text-xs text-[var(--ink-soft)]">
        Weekly live doubt-solving class: every Friday 9:00 PM (BD time) — check Live classes.
      </p>
    </Modal>
  );
}
